import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { fiscal, ApiError } from "../lib/api";
import { useActiveBranch } from "../lib/session";
import { money, uuid } from "../lib/utils";
import { QueryError } from "../components/QueryError";
import { composeReceipt } from "../lib/hardware/receipt";
import { printReceipt, type PrinterConfig } from "../lib/hardware/printer";

const PRINTER: PrinterConfig = { transport: "network", target: "192.168.1.50:9100" };

// Reimprime un tiquete reconstruyendo el contenido desde el server (líneas +
// pagos + factura). En la app nativa va a la térmica ESC/POS; en web abre el
// diálogo de impresión del navegador.
async function reprintTicket(saleId: string, setMsg: (m: string | null) => void) {
  try {
    const [sale, lines, pays, fdoc, org] = await Promise.all([
      supabase.from("sales").select("total, change_given, branch_id").eq("id", saleId).maybeSingle(),
      supabase.from("sale_lines").select("qty, unit_price, line_note, product:products(name)").eq("sale_id", saleId),
      supabase.from("payments").select("method, amount").eq("sale_id", saleId),
      supabase.from("fiscal_documents").select("cufe, authorization_code, status").eq("sale_id", saleId).order("created_at", { ascending: false }).limit(1).maybeSingle(),
      supabase.from("organizations").select("trade_name").maybeSingle(),
    ]);
    if (sale.error || lines.error) throw sale.error ?? lines.error;
    if (!sale.data) throw new Error("venta no encontrada");
    const s = sale.data as unknown as { total: number; change_given: number; branch_id: string };
    const br = (await supabase.from("branches").select("name").eq("id", s.branch_id).maybeSingle()).data as { name?: string } | null;
    const items = ((lines.data ?? []) as unknown as { qty: number; unit_price: number; line_note?: string; product?: { name: string } }[])
      .map((l) => ({ name: l.line_note || l.product?.name || "(producto)", qty: Number(l.qty), unitPrice: Number(l.unit_price) }));
    const payRows = (pays.data ?? []) as { method: string; amount: number }[];
    const paid = payRows.reduce((sum, p) => sum + Number(p.amount), 0);
    const method = payRows.map((p) => p.method).join("+") || "efectivo";
    const fd = fdoc.data as { cufe?: string; authorization_code?: string; status?: string } | null;
    const o = org.data as { trade_name?: string } | null;
    await printReceipt(PRINTER, {
      content: composeReceipt({
        org: o?.trade_name ?? "Ferretería",
        branch: br?.name ?? "Caja",
        items, total: Number(s.total),
        paid, change: Number(s.change_given ?? 0), method,
        cufe: fd?.status === "authorized" ? fd.cufe : undefined,
        authCode: fd?.authorization_code ?? undefined,
      }),
      openDrawer: false, cut: true,
    });
    setMsg("Tiquete enviado a impresión.");
  } catch (e) {
    setMsg(e instanceof Error ? e.message : String(e));
  }
}

// Ventas: historial reciente de la sucursal + DEVOLUCIÓN. La devolución usa
// process_sale_return (reingresa stock + NC referida, idempotente por client_uuid).
export const Route = createFileRoute("/ventas")({ component: Ventas });

interface SaleRow { id: string; total: number; status: string; paid_at: string | null; created_at: string }

const STATUS: Record<string, string> = {
  paid: "Pagada", returned_partial: "Devuelta parcial", returned_total: "Devuelta total",
};

function Ventas() {
  const branch = useActiveBranch();
  const [returnSale, setReturnSale] = useState<string | null>(null);
  const [printMsg, setPrintMsg] = useState<string | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["sales", branch],
    queryFn: async () => {
      const { data, error } = await supabase.from("sales")
        .select("id, total, status, paid_at, created_at")
        .eq("branch_id", branch).in("status", ["paid", "returned_partial", "returned_total"])
        .order("created_at", { ascending: false }).limit(50);
      if (error) throw error;
      return (data ?? []) as SaleRow[];
    },
  });

  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold">Ventas</h1>
      <QueryError error={error} />
      {printMsg && <div className="mb-3 rounded-lg bg-grafito-100 px-4 py-2 text-sm">{printMsg}</div>}
      <div className="overflow-hidden rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-grafito-50 text-left text-grafito-400">
            <tr><th className="px-4 py-2">Venta</th><th className="px-4 py-2">Fecha</th><th className="px-4 py-2 text-right">Total</th><th className="px-4 py-2">Estado</th><th className="px-4 py-2" /></tr>
          </thead>
          <tbody>
            {isLoading ? <tr><td colSpan={5} className="px-4 py-8 text-center text-grafito-400">Cargando…</td></tr>
              : (data ?? []).length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-grafito-400">Sin ventas registradas.</td></tr>
              : data!.map((s) => (
                <tr key={s.id} className="border-t">
                  <td className="px-4 py-2 font-mono text-xs">{s.id.slice(0, 8)}</td>
                  <td className="px-4 py-2 text-xs">{new Date(s.paid_at ?? s.created_at).toLocaleString("es-PA")}</td>
                  <td className="px-4 py-2 text-right">{money(s.total)}</td>
                  <td className="px-4 py-2"><span className="rounded bg-grafito-100 px-2 py-0.5 text-xs">{STATUS[s.status] ?? s.status}</span></td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => reprintTicket(s.id, setPrintMsg)} className="text-xs text-grafito-400 hover:text-brand">🖨 imprimir</button>
                    {s.status !== "returned_total" && <button onClick={() => setReturnSale(s.id)} className="ml-2 text-xs text-grafito-400 hover:text-brand">devolver</button>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {returnSale && <ReturnModal saleId={returnSale} onDone={() => setReturnSale(null)} onCancel={() => setReturnSale(null)} />}
    </div>
  );
}

interface RetLine { sale_line_id: string; product_id: string; name: string; sold: number; alreadyReturned: number; qty: string; restock: boolean }

function ReturnModal({ saleId, onDone, onCancel }: { saleId: string; onDone: () => void; onCancel: () => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [emitNc, setEmitNc] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);

  const { data: lines, isLoading } = useQuery({
    queryKey: ["sale_return_lines", saleId],
    queryFn: async () => {
      const { data: sl } = await supabase.from("sale_lines")
        .select("id, product_id, qty_base, product:products(name)").eq("sale_id", saleId);
      const rows = (sl ?? []) as unknown as { id: string; product_id: string; qty_base: number; product: { name: string } }[];
      // descontar lo ya devuelto por línea (no exceder lo vendido)
      const out: RetLine[] = [];
      for (const l of rows) {
        const { data: prev } = await supabase.from("sale_return_lines")
          .select("qty_base").eq("sale_line_id", l.id);
        const already = (prev ?? []).reduce((s, r) => s + (r as { qty_base: number }).qty_base, 0);
        out.push({ sale_line_id: l.id, product_id: l.product_id, name: l.product.name, sold: l.qty_base, alreadyReturned: already, qty: "0", restock: true });
      }
      return out;
    },
  });

  const [rows, setRows] = useState<RetLine[] | null>(null);
  const data = rows ?? lines ?? [];
  function setRow(i: number, patch: Partial<RetLine>) { setRows((c) => { const base = c ?? lines ?? []; const cp = [...base]; cp[i] = { ...cp[i], ...patch }; return cp; }); }

  const submit = useMutation({
    mutationFn: () => {
      const ret = data.filter((r) => parseFloat(r.qty) > 0)
        .map((r) => ({ sale_line_id: r.sale_line_id, qty_base: parseFloat(r.qty), restock: r.restock }));
      if (ret.length === 0) throw new Error("Indica al menos una cantidad a devolver.");
      return fiscal.processReturn(saleId, uuid(), ret, reason || undefined, emitNc);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sales"] });
      void qc.invalidateQueries({ queryKey: ["stock"] });
      onDone();
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : (e as Error).message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
        <h2 className="mb-4 text-lg font-bold">Devolución de mercadería</h2>
        {msg && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{msg}</div>}
        {isLoading ? <p className="text-grafito-400">Cargando líneas…</p> : (
          <div className="overflow-hidden rounded-xl border">
            <table className="w-full text-sm">
              <thead className="bg-grafito-50 text-left text-grafito-400">
                <tr><th className="px-3 py-2">Producto</th><th className="px-3 py-2 text-right">Vendido</th><th className="px-3 py-2 text-right">Ya devuelto</th><th className="px-3 py-2 text-right">Devolver</th><th className="px-3 py-2">Reingresa</th></tr>
              </thead>
              <tbody>
                {data.map((l, i) => {
                  const max = l.sold - l.alreadyReturned;
                  return (
                    <tr key={l.sale_line_id} className="border-t">
                      <td className="px-3 py-2">{l.name}</td>
                      <td className="px-3 py-2 text-right text-grafito-400">{l.sold}</td>
                      <td className="px-3 py-2 text-right text-grafito-400">{l.alreadyReturned}</td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" min="0" max={max} value={l.qty} onChange={(e) => setRow(i, { qty: e.target.value })}
                          className="w-20 rounded border px-2 py-1 text-right" />
                        <span className="ml-1 text-xs text-grafito-400">/{max}</span>
                      </td>
                      <td className="px-3 py-2"><input type="checkbox" checked={l.restock} onChange={(e) => setRow(i, { restock: e.target.checked })} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo de la devolución" className="mt-3 w-full rounded-lg border px-3 py-2" />
        <label className="mt-3 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={emitNc} onChange={(e) => setEmitNc(e.target.checked)} />
          Emitir Nota de Crédito (si la venta tiene factura autorizada)
        </label>
        <div className="mt-5 flex gap-2">
          <button onClick={onCancel} className="btn btn-ghost flex-1">Cancelar</button>
          <button onClick={() => submit.mutate()} disabled={submit.isPending} className="btn btn-primary flex-1 disabled:opacity-40">
            {submit.isPending ? "Procesando…" : "Confirmar devolución"}
          </button>
        </div>
      </div>
    </div>
  );
}
